"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { useState } from "react"
import { useInView } from "react-intersection-observer"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"

type Props = {
  data: any
}

// Two rows only when 10+ images are uploaded; 9 or fewer stay in a single row
// (ASH-1965).
const SPLIT_THRESHOLD = 10

// --- Icons (white circle, near-black bars per Figma) ---

function PauseIcon() {
  return (
    <svg
      width="20"
      height="23"
      viewBox="0 0 22 25"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <rect x="0" y="0" width="8" height="25" />
      <rect x="14" y="0" width="8" height="25" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg
      width="20"
      height="23"
      viewBox="0 0 22 25"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d="M2 1.5v22l19-11L2 1.5z" />
    </svg>
  )
}

// --- Styled Components ---

const Section = styled.section`
  padding: 40px 0;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 0;
  }
`

/* title + subtitle centered (Figma header align = CENTER) */
const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  padding: 0 20px;
`

const Title = styled.h2`
  font-size: 40px;
  font-weight: 500;
  line-height: 1.2;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  /* emphasized word carries the Figma brush-texture underline (ASH-1960), not a
     plain text-decoration rule. display:inline keeps surrounding spaces intact. */
  strong {
    position: relative;
    display: inline;
    font-weight: 700;
    text-decoration: none;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: 0px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const Subtitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-text);
  margin: 8px 0 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Row = styled.div`
  overflow: hidden;
`

/* duration scales with the item count so every row scrolls at the same px/s
   speed. First row moves left-to-right (RTL), second row right-to-left. */
const Track = styled.div<{ duration: number; paused: boolean; $reverse?: boolean }>`
  @keyframes g4-marquee {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-50%);
    }
  }
  @keyframes g4-marquee-reverse {
    from {
      transform: translateX(-50%);
    }
    to {
      transform: translateX(0);
    }
  }

  display: flex;
  gap: 16px;
  width: max-content;
  animation: ${({ $reverse }) => ($reverse ? "g4-marquee-reverse" : "g4-marquee")}
    ${({ duration }) => duration}s linear infinite;
  animation-play-state: ${({ paused }) => (paused ? "paused" : "running")};
`

/* uniform card: 206x169 mobile, 416x340 desktop (Figma) */
const Card = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 206px;
  aspect-ratio: 416 / 340;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 416px;
  }
`

const CardImage = styled(Image)`
  object-fit: cover;
`

const PlayPauseBtn = styled.button`
  position: absolute;
  bottom: 16px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background-color: #fff;
  color: #020a0d;
  /* Figma renders the control at 40% opacity (ASH-1971); it firms up on hover/focus */
  opacity: 0.4;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
  &:focus-visible {
    opacity: 0.9;
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 50px;
    height: 50px;
  }
`

/* one-time slide-in from left/right as the gallery scrolls into view (ASH-1968).
   Odd rows slide from the start edge, even rows from the end edge. Under reduced
   motion the hook reveals immediately AND the transition is dropped. */
const RevealRow = styled.div<{ $in: boolean; $fromEnd?: boolean }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateX(${({ $in, $fromEnd }) => ($in ? "0%" : $fromEnd ? "-100%" : "100%")});
  transition: opacity 0.7s ease, transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

// --- Helpers ---

function splitRows<T>(items: T[]): T[][] {
  if (items.length >= SPLIT_THRESHOLD) {
    const mid = Math.ceil(items.length / 2)
    return [items.slice(0, mid), items.slice(mid)]
  }
  return [items]
}

// --- Main Component ---

function Gallery4({ data }: Props) {
  const [isPlaying, setIsPlaying] = useState(true)
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0,
    rootMargin: "0px 0px -15% 0px",
  })

  const topTitle = extractProperty(data, "topTitle") as string | ""
  const subtitle = extractProperty(data, "subtitle") as string | ""
  const images = (extractProperty(data, "images", true) as any[] | undefined) || []
  const pics = images.filter((img) => img?.url)
  const rows = splitRows(pics)

  if (!topTitle && pics.length === 0) return null

  return (
    <Section ref={ref}>
      {(topTitle || subtitle) && (
        <Header>
          {topTitle && <Title dangerouslySetInnerHTML={{ __html: topTitle }} />}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Header>
      )}

      {pics.length > 0 && (
        <Content>
          {rows.map((row, r) => (
            <RevealRow key={r} $in={inView} $fromEnd={r % 2 === 1}>
              <Row>
                {/* images duplicated so the marquee loops seamlessly. Both rows run
                  the same direction (ASH-1999). */}
                <Track duration={row.length * 5} paused={!isPlaying} $reverse={r > 0}>
                  {[...row, ...row].map((img, i) => (
                    <Card key={i}>
                      <CardImage
                        src={getUmbracoMediaUrl(img.url)}
                        fill
                        alt={img.altText || ""}
                        sizes="(max-width: 768px) 206px, 416px"
                        aria-hidden={i >= row.length}
                      />
                    </Card>
                  ))}
                </Track>
              </Row>
            </RevealRow>
          ))}

          <PlayPauseBtn
            style={{ left: 20 }}
            onClick={() => setIsPlaying((p) => !p)}
            aria-label={isPlaying ? "עצור תנועת הרכיב" : "הפעל תנועה רכיב"}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </PlayPauseBtn>
        </Content>
      )}
    </Section>
  )
}

export default Gallery4
